import {
  DomainCitizenshipType,
  DomainLandSituationType,
  DomainLender,
} from "@porchpass/api-client";
import type { DtoGetLandResp } from "@porchpass/api-client";
import type { LandData } from "src/components/DealerGeneratedApplication/types/applicationTypes";

/** Optional porchpass eligibility fields that may be present on land GET responses. */
export type DtoGetLandRespWithPorchpass = DtoGetLandResp & {
  block_reasons?: DtoPorchpassBlockReason[];
  supported_by_porchpass?: boolean;
};

/** Mirrors dto.PorchpassBlockReason in services/app/dto/full_application.go. */
export type DtoPorchpassBlockReason =
  "land_and_home" | "out_of_state" | "non_us_citizen";

export const DtoPorchpassBlockReason = {
  BlockReasonLandAndHome: "land_and_home",
  BlockReasonOutOfState: "out_of_state",
  BlockReasonCitizenship: "non_us_citizen",
} as const satisfies Record<string, DtoPorchpassBlockReason>;

/** Keep in sync with services/app/domain/lender.go porchpassSupportedStates */
export const PORCHPASS_SUPPORTED_STATES = ["TX"] as const;

/**
 * Keep in sync with SupportedByPorchpassCitizenship in
 * services/app/dto/full_application.go: one borrower with any of these is enough.
 */
const PORCHPASS_SUPPORTED_CITIZENSHIPS: readonly string[] = [
  DomainCitizenshipType.Citizen,
  DomainCitizenshipType.PermanentResident,
];

export const PORCHPASS_BLOCK_MESSAGES: Record<DtoPorchpassBlockReason, string> =
  {
    [DtoPorchpassBlockReason.BlockReasonLandAndHome]:
      "PorchPass does not currently process land and home loans.",
    [DtoPorchpassBlockReason.BlockReasonOutOfState]:
      "PorchPass does not currently process applications for homes in this state.",
    [DtoPorchpassBlockReason.BlockReasonCitizenship]:
      "PorchPass does not currently process applications unless at least one borrower is a U.S. citizen or permanent resident.",
  };

const BLOCK_REASON_ORDER: DtoPorchpassBlockReason[] = [
  DtoPorchpassBlockReason.BlockReasonLandAndHome,
  DtoPorchpassBlockReason.BlockReasonOutOfState,
  DtoPorchpassBlockReason.BlockReasonCitizenship,
];

export type PorchpassBlockInput = Partial<
  Pick<
    LandData,
    | "landSituation"
    | "useAsDownPayment"
    | "isRefinance"
    | "supportedByPorchpass"
    | "blockReasons"
  >
> & {
  homeState?: string;
  /** Citizenship of every borrower on the application, primary first. */
  borrowerCitizenships?: (DomainCitizenshipType | "" | undefined)[];
};

function resolveInProgressLandAndHome(
  land: PorchpassBlockInput,
): DtoPorchpassBlockReason | undefined {
  const situation = land.landSituation;
  if (!situation) {
    return undefined;
  }

  switch (situation) {
    case DomainLandSituationType.LandSituationTypeAlreadyOwned: {
      if (land.useAsDownPayment === undefined) {
        return undefined;
      }
      return land.useAsDownPayment
        ? DtoPorchpassBlockReason.BlockReasonLandAndHome
        : undefined;
    }

    case DomainLandSituationType.LandSituationTypePurchasing:
      return DtoPorchpassBlockReason.BlockReasonLandAndHome;

    case DomainLandSituationType.LandSituationTypeCurrentlyFinanced: {
      if (
        land.useAsDownPayment === undefined ||
        land.isRefinance === undefined
      ) {
        return undefined;
      }
      return land.useAsDownPayment || land.isRefinance
        ? DtoPorchpassBlockReason.BlockReasonLandAndHome
        : undefined;
    }

    default:
      return undefined;
  }
}

function resolveInProgressOutOfState(
  homeState?: string,
): DtoPorchpassBlockReason | undefined {
  const normalized = homeState?.trim().toUpperCase();
  if (!normalized) {
    return undefined;
  }

  if (
    PORCHPASS_SUPPORTED_STATES.includes(
      normalized as (typeof PORCHPASS_SUPPORTED_STATES)[number],
    )
  ) {
    return undefined;
  }

  return DtoPorchpassBlockReason.BlockReasonOutOfState;
}

function resolveInProgressCitizenship(
  borrowerCitizenships: PorchpassBlockInput["borrowerCitizenships"] = [],
): DtoPorchpassBlockReason | undefined {
  if (
    borrowerCitizenships.some(
      (c) => !!c && PORCHPASS_SUPPORTED_CITIZENSHIPS.includes(c),
    )
  ) {
    return undefined;
  }

  // A borrower who hasn't answered yet may still qualify, so only block once
  // every borrower has answered.
  if (
    borrowerCitizenships.length === 0 ||
    borrowerCitizenships.some((c) => !c)
  ) {
    return undefined;
  }

  return DtoPorchpassBlockReason.BlockReasonCitizenship;
}

function resolveLandAndStateReasons(
  input: PorchpassBlockInput,
): DtoPorchpassBlockReason[] {
  // Use API blockReasons if present, otherwise infer land and state. The API's
  // citizenship reason is dropped because citizenship is always decided
  // client-side from the borrowers as currently entered, which may have
  // changed since.
  if (input.blockReasons?.length) {
    return input.blockReasons.filter(
      (r) => r !== DtoPorchpassBlockReason.BlockReasonCitizenship,
    );
  }

  const reasons: DtoPorchpassBlockReason[] = [];

  const landReason = resolveInProgressLandAndHome(input);
  if (landReason) {
    reasons.push(landReason);
  }

  const stateReason = resolveInProgressOutOfState(input.homeState);
  if (stateReason) {
    reasons.push(stateReason);
  }

  return reasons;
}

export function resolvePorchpassBlockReasons(
  input: PorchpassBlockInput,
): DtoPorchpassBlockReason[] {
  // A positive API result settles land and state, since land edits clear it.
  // It does not settle citizenship: borrower edits leave it in place.
  const reasons =
    input.supportedByPorchpass === true
      ? []
      : resolveLandAndStateReasons(input);

  // Always check citizenship (client-side), regardless of the API result
  const citizenshipReason = resolveInProgressCitizenship(
    input.borrowerCitizenships,
  );
  if (citizenshipReason) {
    reasons.push(citizenshipReason);
  }

  return reasons;
}

export function isPorchpassBlocked(input: PorchpassBlockInput): boolean {
  return resolvePorchpassBlockReasons(input).length > 0;
}

export function formatPorchpassBlockNotice(
  reasons: DtoPorchpassBlockReason[],
): string {
  return BLOCK_REASON_ORDER.filter((reason) => reasons.includes(reason))
    .map((reason) => PORCHPASS_BLOCK_MESSAGES[reason])
    .join(" ");
}

export function getPorchpassLenderSelectionState(input: PorchpassBlockInput) {
  const reasons = resolvePorchpassBlockReasons(input);
  const porchpassBlocked = reasons.length > 0;

  return {
    selectionDisabled: porchpassBlocked,
    selectionDisabledTooltip: porchpassBlocked
      ? formatPorchpassBlockNotice(reasons)
      : undefined,
  };
}

export function isPorchpassLenderBlocked(
  lenderId: string,
  input: PorchpassBlockInput,
): boolean {
  return lenderId === DomainLender.PorchPass && isPorchpassBlocked(input);
}
